import React from 'react';
import { PhoneIcon, MessageCircleIcon } from 'lucide-react';
import { contact } from '../data/site';
type Size = 'sm' | 'md' | 'lg';
const sizeClasses: Record<Size, string> = {
  sm: 'px-4 py-2 text-xs',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base'
};
const base = 'inline-flex items-center justify-center gap-2 rounded-full font-sans font-semibold uppercase tracking-wider transition duration-200 ease-smooth hover:scale-105 active:scale-100 whitespace-nowrap';

/* ---------- Solid buttons: reserved for the sticky header ---------- */

interface SolidProps {
  size?: Size;
  className?: string;
  showNumber?: boolean;
  label?: string;
  phone?: 'header' | 'footer';
}
export function CallButton({
  size = 'sm',
  className = '',
  showNumber = false,
  label = 'Llamar',
  phone = 'header'
}: SolidProps) {
  const href = phone === 'header' ? contact.headerPhoneHref : contact.footerPhoneHref;
  const number = phone === 'header' ? contact.headerPhone : contact.footerPhone;
  return <a href={href} className={`${base} ${sizeClasses[size]} bg-call text-white shadow-[0_8px_30px_-10px_rgba(30,95,224,0.8)] hover:shadow-[0_0_0_4px_rgba(30,95,224,0.25),0_12px_40px_-8px_rgba(30,95,224,0.9)] ${className}`} data-id="element-13" data-mp="2glgr0j2rs77c">
      <PhoneIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-14" data-mp="26wf92ncnzfqq" />
      <span data-id="element-15" data-mp="21xcclrar2hud">{showNumber ? `${label} ${number}` : label}</span>
    </a>;
}
export function WhatsAppButton({
  size = 'sm',
  className = '',
  showNumber = false,
  label = 'WhatsApp'
}: SolidProps) {
  return <a href={contact.whatsappHref} target="_blank" rel="noopener noreferrer" className={`${base} ${sizeClasses[size]} bg-wa text-ink shadow-[0_8px_30px_-10px_rgba(37,211,102,0.8)] hover:shadow-[0_0_0_4px_rgba(37,211,102,0.25),0_12px_40px_-8px_rgba(37,211,102,0.9)] ${className}`} data-id="element-16" data-mp="2ul8agzc22fsq">
      <MessageCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-17" data-mp="1e8az3pn28gi8" />
      <span data-id="element-18" data-mp="197ndxkliitvx">{showNumber ? `${label} ${contact.whatsappNumber}` : label}</span>
    </a>;
}

/* ---------- Glass buttons: every in-page call to action ---------- */

type Tone = 'gold' | 'wa' | 'call';
const toneClasses: Record<Tone, string> = {
  gold: 'border-gold-light/70 text-gold-light shadow-[0_0_20px_-4px_rgba(242,194,48,0.45),inset_0_0_20px_-8px_rgba(242,194,48,0.5)] hover:border-gold-light hover:text-white hover:shadow-[0_0_34px_-2px_rgba(242,194,48,0.75),inset_0_0_26px_-6px_rgba(242,194,48,0.6)]',
  wa: 'border-wa/70 text-white shadow-[0_0_20px_-4px_rgba(37,211,102,0.45),inset_0_0_20px_-8px_rgba(37,211,102,0.5)] hover:border-wa hover:shadow-[0_0_34px_-2px_rgba(37,211,102,0.75),inset_0_0_26px_-6px_rgba(37,211,102,0.6)]',
  call: 'border-[#4d84f0]/70 text-white shadow-[0_0_20px_-4px_rgba(30,95,224,0.5),inset_0_0_20px_-8px_rgba(30,95,224,0.55)] hover:border-[#6b9bff] hover:shadow-[0_0_34px_-2px_rgba(30,95,224,0.8),inset_0_0_26px_-6px_rgba(30,95,224,0.65)]'
};
const iconToneClasses: Record<Tone, string> = {
  gold: 'text-gold-light',
  wa: 'text-wa',
  call: 'text-[#7aa7ff]'
};
const glassSize: Record<'md' | 'lg', string> = {
  md: 'px-5 py-3 text-[13px] sm:px-6 sm:py-3.5 sm:text-sm',
  lg: 'px-6 py-3.5 text-sm sm:px-8 sm:py-4 sm:text-base'
};
const glassBase = 'inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-full border-2 bg-white/5 text-center font-sans font-semibold uppercase leading-tight tracking-wider backdrop-blur-md transition duration-200 ease-smooth hover:scale-[1.04] hover:bg-white/10 active:scale-100 sm:w-auto';
interface GlassButtonProps {
  href: string;
  tone: Tone;
  children: React.ReactNode;
  size?: 'md' | 'lg';
  external?: boolean;
  icon?: 'phone' | 'whatsapp' | 'none';
  className?: string;
}
export function GlassButton({
  href,
  tone,
  children,
  size = 'lg',
  external = false,
  icon = 'none',
  className = ''
}: GlassButtonProps) {
  const Icon = icon === 'phone' ? PhoneIcon : icon === 'whatsapp' ? MessageCircleIcon : null;
  return <a href={href} {...external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {}} className={`${glassBase} ${glassSize[size]} ${toneClasses[tone]} ${className}`} data-id="element-19" data-mp="3dtvdk9kvtoqj">
      {Icon && <Icon className={`h-4 w-4 shrink-0 ${iconToneClasses[tone]}`} aria-hidden="true" data-id="element-20" data-mp="1g359nz5kkh9i" />}
      <span data-id="element-21" data-mp="1kvyhwx08y7qc">{children}</span>
    </a>;
}
export function GlassWhatsApp({
  children = 'WhatsApp',
  href = contact.whatsappHref,
  size = 'lg',
  className = ''
}: {
  children?: React.ReactNode;
  href?: string;
  size?: 'md' | 'lg';
  className?: string;
}) {
  return <GlassButton href={href} tone="wa" size={size} external icon="whatsapp" className={className} data-id="element-22" data-mp="x52j3fxdic9l">
      {children}
    </GlassButton>;
}
export function GlassCall({
  children = 'Llamar',
  phone = 'header',
  size = 'lg',
  className = ''
}: {
  children?: React.ReactNode;
  phone?: 'header' | 'footer';
  size?: 'md' | 'lg';
  className?: string;
}) {
  return <GlassButton href={phone === 'header' ? contact.headerPhoneHref : contact.footerPhoneHref} tone="call" size={size} icon="phone" className={className} data-id="element-23" data-mp="2ij6x6a9nmkjw">
      {children}
    </GlassButton>;
}